import {
  Component,
  Input,
  OnInit,
  WritableSignal,
  signal,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectItem } from '@openng/optimus-ui/api';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { RadioButtonModule } from '@openng/optimus-ui/radiobutton';
import { DOPEntry } from 'src/app/model/crm/daily-operating-plan/dop-entry';
import { AOR } from 'src/app/model/locations/aor';
import { AORSubSystem } from 'src/app/model/locations/aor-subsystem';
import { DopApiService } from 'src/app/services/api/dop-api.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { SelectModule } from '@openng/optimus-ui/select';
import { ButtonModule } from '@openng/optimus-ui/button';
import { EditorModule } from '@openng/optimus-ui/editor';

@Component({
  selector: 'app-dop-entry-form',
  imports: [
    FormsModule,
    ProgressSpinnerComponent,
    RadioButtonModule,
    DialogModule,
    TranslateModule,
    ReactiveFormsModule,
    SelectModule,
    ButtonModule,
    EditorModule,
  ],
  templateUrl: './dop-entry-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './dop-entry-form.component.scss',
})
export class DopEntryFormComponent implements OnInit {
  private _fb = inject(FormBuilder);
  private _dopApi = inject(DopApiService);
  private _translateService = inject(TranslateService);
  private _logAndMessage = inject(LogAndMessageService);

  @Input() showEntryDialog: boolean = false;
  @Input() selectedEntry: DOPEntry | null = null;
  readonly aor = input.required<AOR>();
  readonly aorSubSystem = input.required<AORSubSystem | null>();
  readonly shiftDate = input.required<string>();

  readonly entryDialogHidden = output();
  readonly entryChangedEvent = output();

  entryFormWorking: WritableSignal<boolean> = signal(false);
  entryForm: FormGroup;
  labels: SelectItem[] = [];
  entryFormHeader: WritableSignal<string> = signal('');

  constructor() {
    this.entryForm = this._fb.group({
      active: [true, Validators.required],
      label: [null, Validators.required],
      entryText: [null, Validators.required],
    });
  }

  ngOnInit() {
    this._dopApi.getDOPLabels().subscribe((labels) => {
      this.labels = labels.map((label) => {
        return {
          label: label.labelText,
          value: label.id,
        };
      });
    });
  }

  populateEntryForm(aorAndSubSystem: string): void {
    this._translateService
      .get('DOP.MESSAGES.HEADERS.ENTRY_FORM', { aorAndSubSystem })
      .subscribe((result) => {
        this.entryFormHeader.set(result);
      });

    if (this.selectedEntry) {
      this.entryForm.patchValue({
        active: this.selectedEntry.active,
        label: this.selectedEntry.labelId,
        entryText: this.selectedEntry.entry,
      });
    }
  }

  onEntryDialogHide(): void {
    this.showEntryDialog = false;
    this.entryForm.patchValue({
      active: true,
      label: null,
      entryText: null,
    });
    this.selectedEntry = null;
    this.entryDialogHidden.emit();
  }

  validateForm(): void {
    this.entryForm.updateValueAndValidity();
  }

  saveEntry(): void {
    this.entryFormWorking.set(true);
    const formValue = this.entryForm.getRawValue();
    if (this.selectedEntry) {
      // Edit logic
      this._dopApi
        .updateDOPEntry(this.selectedEntry.id, {
          labelId: formValue.label,
          active: formValue.active,
          entry: formValue.entryText,
        })
        .subscribe(
          (result) => {
            this._logAndMessage.translateToSuccessMessage({
              headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
              bodyKey: 'DOP.MESSAGES.SUCCESS.EDIT_ENTRY',
            });
            this.entryChangedEvent.emit();
            this.onEntryDialogHide();
            this.entryFormWorking.set(false);
          },
          (error) => {
            this._logAndMessage.translateToSuccessMessage({
              headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
              bodyKey: 'DOP.MESSAGES.ERROR.EDIT_ENTRY',
            });
            console.error(error);
            this.entryFormWorking.set(false);
          }
        );
    } else {
      // Create logic
      this._dopApi
        .createDOPEntry({
          aorId: this.aor().id,
          aorSubSystemId: this.aorSubSystem()?.id ?? null,
          shiftDate: this.shiftDate(),
          labelId: formValue.label,
          entry: formValue.entryText,
          active: formValue.active,
        })
        .subscribe(
          (result) => {
            this._logAndMessage.translateToSuccessMessage({
              headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
              bodyKey: 'DOP.MESSAGES.SUCCESS.CREATE_ENTRY',
            });
            this.entryChangedEvent.emit();
            this.onEntryDialogHide();
            this.entryFormWorking.set(false);
          },
          (error) => {
            this._logAndMessage.translateToSuccessMessage({
              headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
              bodyKey: 'DOP.MESSAGES.ERROR.CREATE_ENTRY',
            });
            console.error(error);
            this.entryFormWorking.set(false);
          }
        );
    }

    this.selectedEntry = null;
  }
}
